<template>
  <modal-dialog
    visible
    persistent
    max-width="600px"
    :title="title"
  >
    <!-- Current Image  -->
    <v-form ref="form">
      <template v-if="mode === UploadDialogMode.PREVIEW">
        <v-img :src="imageSrc" />
      </template>
      <!-- Upload Form -->
      <vue-file-agent
        v-if="mode === UploadDialogMode.UPLOAD_FORM"
        v-model="fileRecords"
        theme="list"
        :multiple="false"
        deletable
        meta
        accept="image/*"
        max-size="2MB"
        :max-files="1"
        :help-text="helpLabel"
        :error-text="{
          type: $t('error.upload_invalid_filetype'),
          size: $t('error.upload_file_size_exceeded_maximum_x', ['2MB']),
        }"
      >
        <template #file-preview-new>
          <div
            key="new"
            class="d-flex align-center justify-center flex-column"
            style="height: 200px"
          >
            <v-icon
              x-large
              class="mb-2"
            >
              mdi-image-plus-outline
            </v-icon>
            {{ helpLabel }}
          </div>
        </template>
      </vue-file-agent>
      <!-- After Local Upload  Cropper -->
      <template v-if="mode === UploadDialogMode.EDIT">
        <cropper
          ref="cropperRef"
          classname="cropper"
          :src="previewImage"
          :stencil-props="stencilProps"
          :default-size="defaultSize"
        />
        <div class="text-center mt-2">
          <span>{{ $t('label.upload_image_crop_instructions') }}</span>
        </div>
      </template>
    </v-form>

    <!-- Actions -->
    <template #actions>
      <v-spacer />
      <v-btn
        v-if="mode === UploadDialogMode.PREVIEW"
        outlined
        color="primary"
        @click="replaceImage"
      >
        {{ $t('button.replace_image') }}
      </v-btn>
      <v-btn
        v-if="mode === UploadDialogMode.PREVIEW"
        color="error"
        outlined
        @click="deleteImage"
      >
        <v-icon>mdi-delete</v-icon>
        <span class="d-none d-lg-block">{{ $t('button.delete') }}</span>
      </v-btn>
      <v-btn
        v-if="mode === UploadDialogMode.UPLOAD_FORM || mode === UploadDialogMode.EDIT"
        :disabled="mode === UploadDialogMode.UPLOAD_FORM"
        color="success"
        @click="save"
      >
        {{ $t('button.save') }}
      </v-btn>
      <v-btn
        color="primary"
        text
        @click="close"
      >
        {{ $t('button.close' ) }}
      </v-btn>
    </template>
  </modal-dialog>
</template>

<script setup lang="ts">
import {
  PropType, ref, watch,
} from 'vue';
import { Cropper } from 'vue-advanced-cropper';
import { v4 as uuidv4 } from 'uuid';
import showConfirmationDialog from '@/components/dialogs/confirmation-dialog';
import i18n from '@/plugins/i18n';
import { TranslateResult } from 'vue-i18n';
import Pica from 'pica';
// eslint-disable-next-line import/no-unresolved
import 'vue-advanced-cropper/dist/style.css';

interface UploadedFile {
  file: File;
}

enum UploadDialogMode {
  PREVIEW,
  UPLOAD_FORM,
  EDIT
}
const props = defineProps(
  {
    title: { type: [String, Object] as PropType<string|TranslateResult>, required: false, default: '' },
    imageSrc: { type: String, required: false, default: '' },
    filenamePrefix: { type: String, required: false, default: 'img' },
    helpLabel: { type: [String, Object] as PropType<string|TranslateResult>, required: true },
    deleteConfirmationLabel: { type: [String, Object] as PropType<string|TranslateResult>, required: true },
    stencilProps: { type: Object, required: false, default: () => {} },
    maxWidth: { type: Number, required: false, default: 0 },
  },
);

const emit = defineEmits(['close', 'change']);

const cropperRef = ref<InstanceType<typeof Cropper>>();

const previewImage = ref('');

const fileRecords = ref([] as UploadedFile[]);

const mode = ref(props.imageSrc.length === 0 ? UploadDialogMode.UPLOAD_FORM : UploadDialogMode.PREVIEW);

watch(fileRecords, () => {
  if (!fileRecords.value || fileRecords.value.length === 0) {
    return;
  }
  previewImage.value = URL.createObjectURL(fileRecords.value[0].file);
  mode.value = UploadDialogMode.EDIT;
});

//

const save = async () => {
  if (cropperRef.value === null) { return; }

  const { canvas } = cropperRef.value.getResult();
  if (!canvas) return;

  const shouldResize = props.maxWidth !== 0 && canvas.width > props.maxWidth;

  const origFileName = fileRecords.value[0].file.name;
  const extension = origFileName.slice((origFileName.lastIndexOf('.') - 1 + 2) || Infinity);

  const isPng = extension.toLowerCase() === 'png';
  const contentType = isPng ? 'image/png' : 'image/jpeg';

  const fileName = `${props.filenamePrefix}-${uuidv4()}.${isPng ? 'png' : 'jpg'}`;

  console.log('canvas acquired');
  console.log(`File name: ${origFileName} -> ${fileName}`);

  const getBlob = () => {
    if (shouldResize) {
      // if we need to resize
      const ratio = canvas.width / canvas.height;
      const rCanvas = document.createElement('canvas');
      rCanvas.width = props.maxWidth;
      rCanvas.height = props.maxWidth / ratio;

      console.log(`Resizing (${canvas.width},${canvas.height}) to (${rCanvas.width}, ${rCanvas.height}})`);

      const pica = new Pica();
      return pica.resize(canvas, rCanvas).then((result: HTMLCanvasElement) => pica.toBlob(result, contentType));
    }
    // otherwise pull directly from cropper canvas
    console.log(`Not Resizing (${canvas.width},${canvas.height}) under ${props.maxWidth} or disabled.`);
    return new Promise<Blob>((resolve) => {
      canvas.toBlob((blob: Blob) => {
        resolve(blob);
      });
    });
  };

  const blob = await getBlob();
  if (!blob) { return; }
  const file = new File([blob], fileName, { type: contentType });
  emit('change', file);
};

const replaceImage = () => {
  mode.value = UploadDialogMode.UPLOAD_FORM;
};

const deleteImage = () => {
  showConfirmationDialog(
    undefined,
    props.deleteConfirmationLabel as string,
    { title: i18n.tc('button.yes'), action: () => { emit('change', null); } },
    { title: i18n.tc('button.cancel') },
  );
};

const close = () => {
  emit('close');
};

const defaultSize = ({ imageSize }:
{ imageSize: { width: number; height: number; }}): { width: number; height: number } => ({
  width: imageSize.width,
  height: imageSize.height,
});
</script>

<style lang="scss">

.vue-file-agent {
  &.file-input-wrapper.is-drag-valid.is-drag-over,
  .is-drag-valid.is-drag-over &.file-input-wrapper {
    border-color: $primary-color !important;
    box-shadow: inset 0px 0px 20px 1px $primary-color !important;
  }

  &.file-input-wrapper {
    border: 2px dashed $grey-50-color;
    border-radius: 12px;
  }

  .file-preview-new::before {
    background: transparent !important;
  }

  .image-preview {
    border-radius: 8px;
  }
}

.vue-advanced-cropper .vue-simple-handler {
  .theme--dark & {
    background-color: $white-color;
    border: 1px solid $blue-color;
  }
  background-color: $blue-color;
  border: 1px solid $white-color;
}
</style>
